/* eslint-disable @typescript-eslint/explicit-module-boundary-types */

import { action, makeAutoObservable, reaction, runInAction } from 'mobx';

import ToastifyService from 'src/services/ToastifyService';

import {
  ILoyaltyBonusOperation,
  ILoyaltyBonusOperationFormValues,
  ILoyaltyBonusOperationReasonOption,
  ILoyaltyBonusServiceProvider,
  PossibleOperationType,
} from '../interfaces';
import OperationType = ILoyaltyBonusOperationReasonOption.OperationType;

export class LoyaltyBonusStore {
  private readonly loyaltyBonusService: ILoyaltyBonusServiceProvider;
  private readonly userId: string;

  isBonusFormModalOpened = false;

  totalSum?: number;

  operations: ILoyaltyBonusOperation[] = [];

  page = 0;
  total = 0;
  limit = 10;

  isBonusInfoLoading = false;
  isOperationsLoading = false;
  hasError = false;

  isNewOperationPending = false;
  isNewOperationHasError = false;

  reasonOptionsLoading = false;
  reasonOptionsHasError = false;

  private reasonOptions: ILoyaltyBonusOperationReasonOption[] = [];

  get availableReasonOptions(): ILoyaltyBonusOperationReasonOption[] {
    return this.reasonOptions;
  }

  get isLoading() {
    return this.isBonusInfoLoading || this.isOperationsLoading;
  }

  get isBonusShown(): boolean {
    return this.isBonusInfoLoading || this.isOperationsLoading || !!this.operations?.length;
  }

  constructor({ userId, loyaltyBonusService }: { userId: string; loyaltyBonusService: ILoyaltyBonusServiceProvider }) {
    this.userId = userId;
    this.loyaltyBonusService = loyaltyBonusService;

    makeAutoObservable(this, undefined, { autoBind: true });

    /* eslint-disable @typescript-eslint/no-floating-promises */
    this.fetchLoyaltyBonusInfo();
    this.fetchOperations();
    this.fetchAvailableReasonOptions();
    /* eslint-enable @typescript-eslint/no-floating-promises */
  }

  openBonusModal() {
    this.isBonusFormModalOpened = true;
  }

  closeBonusModal() {
    this.isBonusFormModalOpened = false;
  }

  private async fetchAvailableReasonOptions() {
    if (!this.loyaltyBonusService || this.reasonOptions.length) return;

    this.reasonOptionsLoading = true;

    try {
      const { reasons } = await this.loyaltyBonusService.getAvailableOperationReasons();

      runInAction(() => {
        this.reasonOptionsLoading = false;
        if (reasons) this.reasonOptions = reasons;
      });
    } catch (error) {
      runInAction(() => {
        this.reasonOptionsLoading = false;
        this.reasonOptionsHasError = true;
      });
    }
  }

  private async fetchLoyaltyBonusInfo() {
    this.isBonusInfoLoading = true;
    this.hasError = false;

    try {
      const { total } = await this.loyaltyBonusService.getAccountInfo({
        userId: this.userId,
      });

      runInAction(() => {
        this.totalSum = total as number;
        this.isBonusInfoLoading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.totalSum = undefined;
        this.hasError = true;
        this.isBonusInfoLoading = false;
      });
    }
  }

  private async fetchOperations() {
    this.isOperationsLoading = true;
    this.hasError = false;

    try {
      const { data, limit, page, total } = await this.loyaltyBonusService.getOperations({
        page: this.page,
        limit: this.limit,
        userId: this.userId,
      });

      const operations = (data || []).filter((value) => value.type !== ILoyaltyBonusOperation.Type.Block);

      runInAction(() => {
        this.operations = operations;
        this.limit = limit as number;
        this.page = page as number;
        this.total = total as number;
        this.isOperationsLoading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.operations = [];
        this.hasError = true;
        this.isOperationsLoading = false;
      });
    }
  }

  async createNewOperation(formValue: ILoyaltyBonusOperationFormValues) {
    const { amount, reason, comment } = formValue;

    if (reason == null) return;

    const type = this.getOperationType(formValue.type, reason.possibleOperationType);

    this.isNewOperationPending = true;
    this.isNewOperationHasError = false;

    const request = { amount, reason, userId: this.userId, comment };

    (type === ILoyaltyBonusOperationReasonOption.OperationType.Accrual
      ? this.loyaltyBonusService.accrue(request)
      : this.loyaltyBonusService.chargeOff(request)
    )
      .then(({ account, operations }) => {
        this.closeBonusModal();

        ToastifyService.toast(
          `Баллы успешно ${
            type === ILoyaltyBonusOperationReasonOption.OperationType.Accrual ? 'начислены' : 'списаны'
          }`,
          { type: 'success' },
        );

        if (account) {
          this.totalSum = account.total as number;
        }

        if (operations) this.operations = [...operations, ...this.operations];

        return Promise.all([this.fetchLoyaltyBonusInfo(), this.fetchOperations()]);
      })
      .then(() => {
        this.isNewOperationPending = false;
      })
      .catch(() => {
        this.isNewOperationHasError = true;
        this.isNewOperationPending = false;
      });

    try {
      await Promise.all([this.fetchLoyaltyBonusInfo(), this.fetchOperations()]);
      this.isNewOperationPending = false;
    } catch (e) {
      this.isNewOperationHasError = true;
      this.isNewOperationPending = false;
    }
  }

  getOperationType(type: OperationType, possibleOperationType?: PossibleOperationType): OperationType {
    let operationType = type;

    if (possibleOperationType && possibleOperationType !== OperationType.Both && possibleOperationType !== type) {
      operationType = possibleOperationType;
    }

    return operationType;
  }
}
